import { useState } from 'react';
import { ILot } from '../types/IOrderResponse';

interface SavedLots {
  kitLots?: ILot[];
  boxLots?: ILot[];
}

/** Lots added or removed in `current` since `baseline` (the last list the API returned). */
export const countUnsavedLotChanges = (current: ILot[], baseline: ILot[]) => {
  const added = current.filter((lot) => lot.id == null).length;
  const removed = baseline.filter(
    (saved) => saved.id != null && !current.some((lot) => lot.id === saved.id),
  ).length;
  return added + removed;
};

export interface LotsDraft {
  kitLots: ILot[];
  boxLots: ILot[];
  setKitLots: (lots: ILot[]) => void;
  setBoxLots: (lots: ILot[]) => void;
  /** Additions plus removals not yet saved. */
  unsavedCount: number;
  /** Throw away everything since the last save. */
  discard: () => void;
  /** Bumps on discard; use as the editor's key so half-typed entry rows clear too. */
  editorKey: number;
}

// The unsaved working copy of an order's kit and box lots. It follows `saved` (the order the
// API last returned) whenever that changes, which is after our own save or the first load.
export default function useLotsDraft(saved: SavedLots | undefined): LotsDraft {
  const [kitLots, setKitLots] = useState<ILot[]>(saved?.kitLots || []);
  const [boxLots, setBoxLots] = useState<ILot[]>(saved?.boxLots || []);
  const [editorKey, setEditorKey] = useState(0);

  // Re-sync during render (not in an effect) so a fresh `saved` never paints one frame of
  // "N unsaved" against the old draft.
  const [syncedTo, setSyncedTo] = useState(saved);
  if (saved !== syncedTo) {
    setSyncedTo(saved);
    if (saved) {
      setKitLots(saved.kitLots || []);
      setBoxLots(saved.boxLots || []);
    }
  }

  const unsavedCount =
    countUnsavedLotChanges(kitLots, saved?.kitLots ?? []) +
    countUnsavedLotChanges(boxLots, saved?.boxLots ?? []);

  const discard = () => {
    setKitLots(saved?.kitLots || []);
    setBoxLots(saved?.boxLots || []);
    setEditorKey((key) => key + 1);
  };

  return {
    kitLots,
    boxLots,
    setKitLots,
    setBoxLots,
    unsavedCount,
    discard,
    editorKey,
  };
}
